import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  Paper,
  CircularProgress,
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ToggleButtonGroup,
  ToggleButton,
  LinearProgress,
  Avatar,
  Switch,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Folder as FolderIcon,
  OndemandVideo as VideoIcon,
  ArrowUpward as UpIcon,
  DragIndicator as DragIcon,
  CloudUpload as UploadIcon,
} from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { toast } from 'react-toastify';
import videoService from '../../services/video.service';
import videoContainerService from '../../services/videoContainer.service';
import courseService from '../../services/course.service';
import { Course } from '../../types';
import { registerBeforeTabChange, unregisterBeforeTabChange } from './Workshop';
import QuickRecapBadge from '../../components/QuickRecapBadge';
import QuickRecapModal from '../../components/QuickRecapModal';

interface VideosProps {
  urlContainerId?: number | null;
  onNavChange?: (containerId: number | null, itemId: number | null, label?: string) => void;
}

const Videos: React.FC<VideosProps> = ({ urlContainerId, onNavChange }) => {
  const [videos, setVideos] = useState<any[]>([]);
  const [containers, setContainers] = useState<any[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [selectedVideo, setSelectedVideo] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);
  const [currentContainer, setCurrentContainer] = useState<number | null>(null);
  const parentContainer = null; // Parent container is always null at root level

  // Sync from URL (back/forward navigation)
  useEffect(() => {
    if (urlContainerId !== undefined && urlContainerId !== currentContainer) {
      setCurrentContainer(urlContainerId);
    }
  }, [urlContainerId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [videoPreviewUrl, setVideoPreviewUrl] = useState<string>('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  // File input ref
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Ref to track current values for saving on unmount
  const currentVideoRef = useRef<{
    video: any | null;
    description: string;
  }>({
    video: null,
    description: ''
  });

  // Update ref whenever values change
  useEffect(() => {
    currentVideoRef.current = {
      video: selectedVideo,
      description
    };
  }, [selectedVideo, description]);

  // Dialogs
  const [videoDialogOpen, setVideoDialogOpen] = useState(false);
  const [createType, setCreateType] = useState<'container' | 'video'>('video');
  const [newContainerTitle, setNewContainerTitle] = useState('');

  // Edit container dialog
  const [editContainerDialogOpen, setEditContainerDialogOpen] = useState(false);
  const [editingContainer, setEditingContainer] = useState<any | null>(null);
  const [editContainerTitle, setEditContainerTitle] = useState('');

  // Edit video dialog
  const [editVideoDialogOpen, setEditVideoDialogOpen] = useState(false);
  const [editingVideo, setEditingVideo] = useState<any | null>(null);
  const [editVideoDescription, setEditVideoDescription] = useState('');

  // Quick Recap modal
  const [quickRecapModalOpen, setQuickRecapModalOpen] = useState(false);
  const [quickRecapVideo, setQuickRecapVideo] = useState<any | null>(null);

  const initialLoadDone = useRef(false);

  const loadVideos = async () => {
    try {
      if (!initialLoadDone.current) setLoading(true);
      const containerParam = currentContainer === null ? 'null' : currentContainer;
      const data = await videoService.getAll({ container: containerParam });
      setVideos(data);
    } catch (error) {
      console.error('Error loading videos:', error);
      toast.error('Erreur lors du chargement des vidéos');
    } finally {
      setLoading(false);
      initialLoadDone.current = true;
    }
  };

  const loadContainers = async () => {
    try {
      const parentParam = currentContainer === null ? 'null' : currentContainer;
      const data = await videoContainerService.getAll({ parent: parentParam });
      setContainers(data);
    } catch (error) {
      console.error('Error loading containers:', error);
      toast.error('Erreur lors du chargement des contenants');
    }
  };

  useEffect(() => {
    loadVideos();
    loadContainers();
    // Load courses for quick recap (only once, not dependent on container)
    const loadCoursesForQuickRecap = async () => {
      try {
        const courseData = await courseService.getCourses();
        setCourses(Array.isArray(courseData) ? courseData : []);
      } catch (error) {
        console.error('Error loading courses:', error);
      }
    };
    loadCoursesForQuickRecap();
  }, [currentContainer]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      if (videoPreviewUrl && videoPreviewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(videoPreviewUrl);
      }
    };
  }, [videoPreviewUrl]);

  // Auto-save description with 2 second delay
  useEffect(() => {
    if (!selectedVideo || !title) return;

    const timeoutId = setTimeout(async () => {
      try {
        await videoService.update(selectedVideo.id, { description });

        // Update selected video
        const updatedVideo = { ...selectedVideo, description };
        setSelectedVideo(updatedVideo);

        // Update video in the list
        setVideos(vids => vids.map(v => v.id === selectedVideo.id ? updatedVideo : v));

        console.log('Video description auto-saved');
      } catch (error) {
        console.error('Error auto-saving video description:', error);
      }
    }, 2000);

    return () => clearTimeout(timeoutId);
  }, [description, selectedVideo, title]);

  // Register callback to save before tab change
  useEffect(() => {
    const saveBeforeTabChange = async () => {
      const current = currentVideoRef.current;
      if (current.video && current.video.description !== current.description) {
        try {
          await videoService.update(current.video.id, { description: current.description });
          console.log('Video saved before tab change');
        } catch (error) {
          console.error('Error saving video before tab change:', error);
        }
      }
    };

    registerBeforeTabChange('videos', saveBeforeTabChange);

    return () => {
      unregisterBeforeTabChange('videos');
    };
  }, []); // Empty dependency array - only register/unregister on mount/unmount

  // Save on component unmount (for navigation outside Workshop)
  useEffect(() => {
    return () => {
      const current = currentVideoRef.current;
      if (current.video && current.video.description !== current.description) {
        // Fire off the save request without awaiting
        // The browser will attempt to complete it even during navigation
        videoService.update(current.video.id, { description: current.description })
          .then(() => console.log('Video saved on unmount'))
          .catch(err => console.error('Error saving video on unmount:', err));
      }
    };
  }, []); // Empty dependency array - only run on unmount

  const handleSelectVideo = async (video: any) => {
    // Save current video before switching
    if (selectedVideo && title && description !== selectedVideo.description) {
      try {
        await videoService.update(selectedVideo.id, { description });

        // Update selected video
        const updatedVideo = { ...selectedVideo, description };
        setSelectedVideo(updatedVideo);

        // Update video in the list
        setVideos(vids => vids.map(v => v.id === selectedVideo.id ? updatedVideo : v));

        console.log('Saved video before switching');
      } catch (error) {
        console.error('Error saving video:', error);
      }
    }

    setSelectedVideo(video);
    setTitle(video.title);
    setDescription(video.description || '');
    setVideoFile(null);
    setVideoPreviewUrl('');
  };

  const handleQuickRecapClick = (video: any) => {
    setQuickRecapVideo(video);
    setQuickRecapModalOpen(true);
  };

  const handleSaveQuickRecapCourse = async (courseId: number | null) => {
    if (!quickRecapVideo) return;

    await videoService.update(quickRecapVideo.id, {
      quick_recap_course: courseId
    });

    toast.success('Référence cours mise à jour');

    const updatedVideo = {
      ...quickRecapVideo,
      quick_recap_course: courseId,
      quick_recap_course_title: courses.find(c => c.id === courseId)?.title || null
    };

    setQuickRecapVideo(updatedVideo);

    if (selectedVideo?.id === quickRecapVideo.id) {
      setSelectedVideo(updatedVideo);
    }

    setVideos(vids => vids.map(v => v.id === quickRecapVideo.id ? updatedVideo : v));
  };

  const handleSaveQuickRecapVideo = async (videoId: number | null) => {
    // Note: Videos can't reference themselves as quick recap videos in most cases
    // This is kept for API consistency
    if (!quickRecapVideo) return;

    await videoService.update(quickRecapVideo.id, {
      quick_recap_video: videoId
    });

    toast.success('Référence vidéo mise à jour');

    const updatedVideo = {
      ...quickRecapVideo,
      quick_recap_video: videoId,
      quick_recap_video_title: videos.find(v => v.id === videoId)?.title || null
    };

    setQuickRecapVideo(updatedVideo);

    if (selectedVideo?.id === quickRecapVideo.id) {
      setSelectedVideo(updatedVideo);
    }

    setVideos(vids => vids.map(v => v.id === quickRecapVideo.id ? updatedVideo : v));
  };

  const handleNavigateToContainer = async (containerId: number | null) => {
    // Save current video before navigating
    if (selectedVideo && title && description !== selectedVideo.description) {
      try {
        await videoService.update(selectedVideo.id, { description });

        // Update selected video
        const updatedVideo = { ...selectedVideo, description };
        setSelectedVideo(updatedVideo);

        // Update video in the list
        setVideos(vids => vids.map(v => v.id === selectedVideo.id ? updatedVideo : v));

        console.log('Saved video before navigating');
      } catch (error) {
        console.error('Error saving video:', error);
      }
    }

    setCurrentContainer(containerId);
    const containerLabel = containerId !== null ? containers.find(c => c.id === containerId)?.title : undefined;
    onNavChange?.(containerId, null, containerLabel);
  };

  const handleFileSelect = (file: File) => {
    // Validate file type
    if (!file.type.startsWith('video/')) {
      toast.error('Veuillez sélectionner un fichier vidéo valide');
      return;
    }

    setVideoFile(file);

    // Create preview URL
    const previewUrl = URL.createObjectURL(file);
    setVideoPreviewUrl(previewUrl);

    // Set title from filename if not already set
    if (!title) {
      const fileName = file.name.replace(/\.[^/.]+$/, ''); // Remove extension
      setTitle(fileName);
    }

    toast.success(`Fichier sélectionné: ${file.name}`);
  };

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);

    const files = event.dataTransfer.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  const handleSave = async () => {
    if (!title.trim()) {
      toast.error('Le titre est requis');
      return;
    }

    if (!videoFile && !selectedVideo) {
      toast.error('Veuillez sélectionner un fichier vidéo');
      return;
    }

    try {
      setUploading(true);
      setUploadProgress(0);

      if (selectedVideo) {
        // Update existing video
        if (videoFile) {
          // If new file is uploaded, send as FormData
          const formData = new FormData();
          formData.append('title', title);
          formData.append('video_file', videoFile);
          if (currentContainer !== null) {
            formData.append('container', currentContainer.toString());
          }
          await videoService.update(selectedVideo.id, formData);
        } else {
          // Just update title (description is edited in the right panel)
          await videoService.update(selectedVideo.id, {
            title,
            container: currentContainer,
          });
        }
        toast.success('Vidéo mise à jour');
        // Reload to get updated data
        loadVideos();
      } else {
        // Create new video
        if (!videoFile) {
          toast.error('Veuillez sélectionner un fichier vidéo');
          return;
        }

        const formData = new FormData();
        formData.append('title', title);
        formData.append('description', description);
        formData.append('video_file', videoFile);

        // Simulate upload progress
        setUploadProgress(50);

        if (currentContainer !== null) {
          formData.append('container', currentContainer.toString());
        }

        await videoService.create(formData);
        setUploadProgress(100);
        toast.success('Vidéo créée avec succès');
      }

      resetForm();
      loadVideos();
    } catch (error) {
      console.error('Error saving video:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDelete = async (videoId: number) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette vidéo ?')) {
      return;
    }

    try {
      await videoService.delete(videoId);
      toast.success('Vidéo supprimée');
      resetForm();
      loadVideos();
    } catch (error) {
      console.error('Error deleting video:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleTogglePublicVideo = async (videoId: number, newIsPublic: boolean) => {
    try {
      await videoService.update(videoId, { is_public: newIsPublic });
      toast.success(`Vidéo rendue ${newIsPublic ? 'publique' : 'privée'}`);

      // Update in the list
      setVideos(vids => vids.map(v => v.id === videoId ? { ...v, is_public: newIsPublic } : v));

      // Update selected video if it's the same
      if (selectedVideo?.id === videoId) {
        setSelectedVideo({ ...selectedVideo, is_public: newIsPublic });
      }
    } catch (error) {
      console.error('Error updating video visibility:', error);
      toast.error('Erreur lors de la mise à jour de la visibilité');
    }
  };

  const handleNew = () => {
    resetForm(); // Start with clean form
    setVideoDialogOpen(true);
    setCreateType('video'); // Default to video creation
  };

  const handleCreateContainer = async () => {
    if (!newContainerTitle.trim()) {
      toast.error('Le nom du contenant est requis');
      return;
    }

    try {
      await videoContainerService.create({
        title: newContainerTitle,
        parent: currentContainer,
      });
      toast.success('Contenant créé');
      setVideoDialogOpen(false);
      setNewContainerTitle('');
      loadContainers();
    } catch (error) {
      console.error('Error creating container:', error);
      toast.error('Erreur lors de la création du contenant');
    }
  };

  const handleEditContainer = (container: any) => {
    setEditingContainer(container);
    setEditContainerTitle(container.title);
    setEditContainerDialogOpen(true);
  };

  const handleUpdateContainer = async () => {
    if (!editContainerTitle.trim()) {
      toast.error('Le nom du contenant est requis');
      return;
    }

    try {
      await videoContainerService.update(editingContainer.id, {
        title: editContainerTitle,
      });
      toast.success('Contenant mis à jour');
      setEditContainerDialogOpen(false);
      setEditingContainer(null);
      setEditContainerTitle('');
      loadContainers();
    } catch (error) {
      console.error('Error updating container:', error);
      toast.error('Erreur lors de la mise à jour du contenant');
    }
  };

  const handleEditVideo = (video: any) => {
    setEditingVideo(video);
    setEditVideoDescription(video.description || '');
    setEditVideoDialogOpen(true);
  };

  const handleUpdateVideoDescription = async () => {
    try {
      await videoService.update(editingVideo.id, {
        description: editVideoDescription,
      });
      toast.success('Vidéo mise à jour');
      loadVideos();
      setEditVideoDialogOpen(false);
      setEditingVideo(null);
      setEditVideoDescription('');

      // Update selected video if it's the one being edited
      if (selectedVideo?.id === editingVideo.id) {
        setSelectedVideo({ ...selectedVideo, description: editVideoDescription });
        setDescription(editVideoDescription);
      }
    } catch (error) {
      console.error('Error updating video:', error);
      toast.error('Erreur lors de la mise à jour de la vidéo');
    }
  };

  const handleDeleteContainer = async (containerId: number) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce contenant ?')) {
      return;
    }

    try {
      await videoContainerService.delete(containerId);
      toast.success('Contenant supprimé');
      loadContainers();
    } catch (error) {
      console.error('Error deleting container:', error);
      toast.error('Erreur lors de la suppression du contenant');
    }
  };

  const handleDragEnd = async (result: any) => {
    const { source, destination, draggableId } = result;

    // If no destination, do nothing
    if (!destination) return;

    const isVideo = draggableId.startsWith('video-');

    // Check if we're dropping a video onto the parent (..)
    if (isVideo && destination.droppableId === 'parent-drop') {
      const videoId = parseInt(draggableId.replace('video-', ''));

      try {
        await videoService.update(videoId, { container: parentContainer });
        toast.success('Vidéo déplacée vers le niveau parent');
        loadVideos();
        loadContainers();
      } catch (error) {
        console.error('Error moving video to parent:', error);
        toast.error('Erreur lors du déplacement');
      }
      return;
    }

    // Check if we're dropping a video onto a container
    if (isVideo && destination.droppableId.startsWith('container-drop-')) {
      const videoId = parseInt(draggableId.replace('video-', ''));
      const containerId = parseInt(destination.droppableId.replace('container-drop-', ''));

      try {
        await videoService.update(videoId, { container: containerId });
        toast.success('Vidéo déplacée dans le contenant');
        loadVideos();
        loadContainers();
      } catch (error) {
        console.error('Error moving video to container:', error);
        toast.error('Erreur lors du déplacement');
      }
      return;
    }

    // If dropped in the same position, do nothing
    if (source.droppableId === destination.droppableId && source.index === destination.index) {
      return;
    }

    // Reorder videos within the same list
    if (isVideo && source.droppableId === 'videos' && destination.droppableId === 'videos') {
      const items = Array.from(videos);
      const [reorderedItem] = items.splice(source.index, 1);
      items.splice(destination.index, 0, reorderedItem);

      // Update order property
      const updatedItems = items.map((item, index) => ({
        ...item,
        order: index,
      }));

      setVideos(updatedItems);

      try {
        await videoService.reorder(updatedItems.map(item => ({ id: item.id, order: item.order })));
      } catch (error) {
        console.error('Error reordering videos:', error);
        toast.error('Erreur lors de la réorganisation');
        loadVideos(); // Reload to revert
      }
    }
  };

  const resetForm = () => {
    setSelectedVideo(null);
    setTitle('');
    setDescription('');
    setVideoFile(null);
    setVideoPreviewUrl('');
    setUploadProgress(0);
    setUploading(false);

    // Revoke object URL to prevent memory leaks
    if (videoPreviewUrl) {
      URL.revokeObjectURL(videoPreviewUrl);
    }
  };

  return (
    <Box>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Grid container spacing={3}>
          {/* Left Panel - Videos List */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, height: 'calc(100vh - 250px)', overflow: 'auto' }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Vidéos</Typography>
                <Button
                  startIcon={<AddIcon />}
                  onClick={handleNew}
                  size="small"
                >
                  Nouveau
                </Button>
              </Box>

              {/* Parent navigation */}
              {currentContainer !== null && (
                <Droppable droppableId="parent-drop" type="VIDEO">
                  {(provided, snapshot) => (
                    <div ref={provided.innerRef} {...provided.droppableProps}>
                      <Card
                        sx={{
                          mb: 1,
                          cursor: 'pointer',
                          backgroundColor: snapshot.isDraggingOver ? 'warning.light' : 'action.hover',
                          border: snapshot.isDraggingOver ? '2px solid' : 'none',
                          borderColor: snapshot.isDraggingOver ? 'warning.main' : 'transparent',
                          transition: 'all 0.2s',
                        }}
                        onClick={() => handleNavigateToContainer(parentContainer)}
                      >
                        <CardContent sx={{ py: 1 }}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <UpIcon color="action" />
                            <Typography variant="body2" fontWeight="bold">..</Typography>
                          </Box>
                        </CardContent>
                      </Card>
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              )}

              {/* Containers */}
              {containers.map((container, containerIndex) => (
                <Droppable key={`container-droppable-${container.id}`} droppableId={`container-drop-${container.id}`} type="VIDEO">
                  {(provided, snapshot) => (
                    <div ref={provided.innerRef} {...provided.droppableProps}>
                      <Card
                        sx={{
                          mb: 1,
                          cursor: 'pointer',
                          border: '2px dashed',
                          borderColor: snapshot.isDraggingOver ? 'success.main' : 'primary.main',
                          backgroundColor: snapshot.isDraggingOver ? 'success.light' : 'inherit',
                          transition: 'all 0.2s',
                        }}
                        onClick={() => handleNavigateToContainer(container.id)}
                      >
                        <CardContent sx={{ py: 1 }}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <FolderIcon color="primary" />
                            <Typography variant="body2" sx={{ flex: 1 }}>
                              {container.title}
                            </Typography>
                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleEditContainer(container); }}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDeleteContainer(container.id); }} sx={{ color: 'error.main' }}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </CardContent>
                      </Card>
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              ))}

              {/* Videos */}
              {loading ? (
                <Box display="flex" justifyContent="center" p={3}>
                  <CircularProgress />
                </Box>
              ) : (
                <Droppable droppableId="videos" type="VIDEO">
                  {(provided) => (
                    <div {...provided.droppableProps} ref={provided.innerRef}>
                      {videos.map((video, videoIndex) => (
                        <Draggable key={`video-${video.id}`} draggableId={`video-${video.id}`} index={videoIndex}>
                          {(provided, snapshot) => (
                            <Card
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              sx={{
                                mb: 1,
                                cursor: 'pointer',
                                backgroundColor: selectedVideo?.id === video.id ? 'action.selected' : snapshot.isDragging ? 'action.hover' : video.is_public ? 'rgba(33, 150, 243, 0.08)' : 'inherit',
                              }}
                              onClick={() => handleSelectVideo(video)}
                            >
                              <CardContent sx={{ py: 1 }}>
                                <Box display="flex" alignItems="center" gap={1}>
                                  <Box {...provided.dragHandleProps}>
                                    <DragIcon sx={{ cursor: 'grab', color: 'action.active', fontSize: 20 }} />
                                  </Box>
                                  {video.thumbnail_url ? (
                                    <Avatar
                                      src={video.thumbnail_url}
                                      alt={video.title}
                                      variant="rounded"
                                      sx={{ width: 40, height: 40 }}
                                    />
                                  ) : (
                                    <Avatar variant="rounded" sx={{ width: 40, height: 40, bgcolor: 'action.hover' }}>
                                      <VideoIcon sx={{ fontSize: 20 }} />
                                    </Avatar>
                                  )}
                                  <Typography variant="body2" sx={{ flex: 1 }}>
                                    {video.title}
                                  </Typography>
                                  <Box display="flex" gap={0.5} alignItems="center">
                                    <QuickRecapBadge
                                      hasCourse={!!video.quick_recap_course}
                                      hasVideo={!!video.quick_recap_video}
                                      onClick={() => handleQuickRecapClick(video)}
                                      size="small"
                                    />
                                    <Switch
                                      size="small"
                                      checked={video.is_public || false}
                                      onClick={(e) => e.stopPropagation()}
                                      onChange={(e) => {
                                        e.stopPropagation();
                                        handleTogglePublicVideo(video.id, e.target.checked);
                                      }}
                                      sx={{ ml: 0.5 }}
                                    />
                                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleEditVideo(video); }}>
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDelete(video.id); }} sx={{ color: 'error.main' }}>
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Box>
                                </Box>
                              </CardContent>
                            </Card>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              )}

              {videos.length === 0 && !loading && (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                  Aucune vidéo disponible
                </Typography>
              )}
            </Paper>
          </Grid>

          {/* Right Panel - Video Preview */}
          <Grid item xs={12} md={8}>
            <Paper sx={{
              p: 2,
              height: 'calc(100vh - 250px)',
              overflow: 'auto',
              backgroundColor: selectedVideo?.is_public ? 'rgba(33, 150, 243, 0.08)' : 'background.paper',
              borderRadius: 2
            }}>
              {selectedVideo ? (
                <>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6">
                      {selectedVideo.title}
                    </Typography>
                    <Box>
                      <IconButton onClick={() => handleEditVideo(selectedVideo)}>
                        <EditIcon />
                      </IconButton>
                    </Box>
                  </Box>

                  <TextField
                    fullWidth
                    label="Description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    margin="normal"
                    multiline
                    rows={4}
                  />

                  {selectedVideo.video_file_url && (
                    <Box mb={3}>
                      <Typography variant="subtitle1" gutterBottom>
                        Vidéo
                      </Typography>
                      <Box
                        sx={{
                          position: 'relative',
                          paddingTop: '56.25%', // 16:9 aspect ratio
                          backgroundColor: 'action.hover',
                          borderRadius: 1,
                        }}
                      >
                        <video
                          controls
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            borderRadius: 4,
                          }}
                          src={selectedVideo.video_file_url}
                        >
                          Votre navigateur ne supporte pas la lecture de cette vidéo.
                        </video>
                      </Box>
                    </Box>
                  )}

                </>
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    color: 'text.secondary'
                  }}
                >
                  <VideoIcon sx={{ fontSize: 64, mb: 2, opacity: 0.3 }} />
                  <Typography variant="h6" gutterBottom>
                    Aucune vidéo sélectionnée
                  </Typography>
                  <Typography variant="body2" color="text.secondary" textAlign="center">
                    Sélectionnez une vidéo dans la liste ou cliquez sur "Nouveau" pour en ajouter une
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      </DragDropContext>

      {/* Creation Dialog */}
      <Dialog
        open={videoDialogOpen}
        onClose={() => {
          setVideoDialogOpen(false);
          setNewContainerTitle('');
          if (!selectedVideo) {
            resetForm();
          }
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{selectedVideo ? 'Modifier la vidéo' : 'Nouveau'}</DialogTitle>
        <DialogContent>
          {!selectedVideo && (
            <Box sx={{ mb: 2, mt: 1 }}>
              <ToggleButtonGroup
                value={createType}
                exclusive
                onChange={(e, newValue) => newValue && setCreateType(newValue)}
                fullWidth
              >
                <ToggleButton value="container">
                  <FolderIcon sx={{ mr: 1 }} />
                  Contenant
                </ToggleButton>
                <ToggleButton value="video">
                  <VideoIcon sx={{ mr: 1 }} />
                  Nouvelle vidéo
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          )}

          {createType === 'container' && (
            <TextField
              autoFocus
              margin="dense"
              label="Nom du contenant"
              fullWidth
              variant="outlined"
              value={newContainerTitle}
              onChange={(e) => setNewContainerTitle(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleCreateContainer();
                }
              }}
            />
          )}

          {createType === 'video' && (
            <Box>
              <TextField
                fullWidth
                label="Titre"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                margin="normal"
                required
              />

              {/* Hidden file input */}
              <input
                ref={fileInputRef}
                type="file"
                accept="video/*"
                style={{ display: 'none' }}
                onChange={handleFileInputChange}
              />

              {/* Drag and drop zone or URL input */}
              {!videoFile && !videoPreviewUrl ? (
                <>
                  <Box
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    sx={{
                      border: '2px dashed',
                      borderColor: isDragging ? 'primary.main' : 'grey.400',
                      borderRadius: 2,
                      p: 3,
                      mt: 2,
                      textAlign: 'center',
                      backgroundColor: isDragging ? 'action.hover' : 'transparent',
                      cursor: 'pointer',
                      transition: 'all 0.3s',
                      '&:hover': {
                        borderColor: 'primary.main',
                        backgroundColor: 'action.hover',
                      },
                    }}
                    onClick={handleBrowseClick}
                  >
                    <UploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                    <Typography variant="h6" gutterBottom>
                      Glissez-déposez votre vidéo ici
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      ou
                    </Typography>
                    <Button variant="outlined" sx={{ mt: 1 }}>
                      Parcourir les fichiers
                    </Button>
                    <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 2 }}>
                      Formats acceptés: MP4, AVI, MOV, MKV
                    </Typography>
                  </Box>
                </>
              ) : (
                <Box sx={{ mt: 2 }}>
                  {videoFile && (
                    <Card sx={{ backgroundColor: 'action.hover', mb: 2 }}>
                      <CardContent>
                        <Box display="flex" alignItems="center" gap={1} mb={2}>
                          <VideoIcon color="primary" />
                          <Typography variant="body2" sx={{ flex: 1 }}>
                            {videoFile.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {(videoFile.size / (1024 * 1024)).toFixed(2)} MB
                          </Typography>
                          <IconButton size="small" onClick={() => {
                            setVideoFile(null);
                            setVideoPreviewUrl('');
                            if (videoPreviewUrl) {
                              URL.revokeObjectURL(videoPreviewUrl);
                            }
                          }}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        {uploading && (
                          <Box sx={{ width: '100%' }}>
                            <LinearProgress variant="determinate" value={uploadProgress} />
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                              Upload en cours: {uploadProgress}%
                            </Typography>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  )}
                </Box>
              )}

              {/* Description field only for new videos, not for editing */}
              {!selectedVideo && (
                <TextField
                  fullWidth
                  label="Description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  margin="normal"
                  multiline
                  rows={4}
                />
              )}

              {videoPreviewUrl && (
                <Box mt={3}>
                  <Typography variant="subtitle1" gutterBottom>
                    Aperçu
                  </Typography>
                  <Box
                    sx={{
                      position: 'relative',
                      paddingTop: '56.25%', // 16:9 aspect ratio
                      backgroundColor: 'action.hover',
                      borderRadius: 1,
                    }}
                  >
                    <video
                      controls
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        borderRadius: 4,
                      }}
                      src={videoPreviewUrl}
                    >
                      Votre navigateur ne supporte pas la lecture de cette vidéo.
                    </video>
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setVideoDialogOpen(false);
              setNewContainerTitle('');
              if (!selectedVideo) {
                resetForm();
              }
            }}
            disabled={uploading}
          >
            Annuler
          </Button>
          {createType === 'container' && (
            <Button variant="contained" onClick={handleCreateContainer}>Créer</Button>
          )}
          {createType === 'video' && (
            <Button
              variant="contained"
              onClick={async () => {
                await handleSave();
                setVideoDialogOpen(false);
              }}
              disabled={!title.trim() || (!videoFile && !selectedVideo) || uploading}
            >
              {uploading ? 'Upload en cours...' : (selectedVideo ? 'Mettre à jour' : 'Créer')}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Edit Container Dialog */}
      <Dialog open={editContainerDialogOpen} onClose={() => setEditContainerDialogOpen(false)}>
        <DialogTitle>Modifier le contenant</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nom du contenant"
            fullWidth
            variant="outlined"
            value={editContainerTitle}
            onChange={(e) => setEditContainerTitle(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && editContainerTitle.trim()) {
                handleUpdateContainer();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setEditContainerDialogOpen(false);
            setEditingContainer(null);
            setEditContainerTitle('');
          }}>Annuler</Button>
          <Button variant="contained" onClick={handleUpdateContainer}>Modifier</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Video Dialog */}
      <Dialog open={editVideoDialogOpen} onClose={() => setEditVideoDialogOpen(false)}>
        <DialogTitle>Modifier la vidéo</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Description de la vidéo"
            fullWidth
            variant="outlined"
            multiline
            rows={4}
            value={editVideoDescription}
            onChange={(e) => setEditVideoDescription(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setEditVideoDialogOpen(false);
            setEditingVideo(null);
            setEditVideoDescription('');
          }}>Annuler</Button>
          <Button variant="contained" onClick={handleUpdateVideoDescription}>Modifier</Button>
        </DialogActions>
      </Dialog>

      {/* Quick Recap Modal */}
      <QuickRecapModal
        open={quickRecapModalOpen}
        onClose={() => setQuickRecapModalOpen(false)}
        itemTitle={quickRecapVideo?.title || ''}
        courses={courses}
        videos={videos.filter(v => v.id !== quickRecapVideo?.id)}
        selectedCourseId={quickRecapVideo?.quick_recap_course}
        selectedVideoId={quickRecapVideo?.quick_recap_video}
        onSaveCourse={handleSaveQuickRecapCourse}
        onSaveVideo={handleSaveQuickRecapVideo}
      />
    </Box>
  );
};

export default Videos;
